import { Button, Stack } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { ModuleInput } from "./ModuleInput";
import { useIsBasicRole } from "../../../hooks";
import { useState } from "react";

/**
 * @param {string} jobName
 * @param {Object<string, {
 *   position: number|null,
 *   type: "string"|"float"|"int"|"boolean",
 *   format: null|"folder"|"file",
 *   nargs: number|"+"|"?"|null,
 *   choices: null|string[]|number[],
 *   argName: string,
 *   description: string,
 *   displayLevel: "alwaysDisplayed"|"collapseDisplayed"|"onlyAdmin"|"neverDisplayed",
 * }>} moduleVersionInputs
 * @param {boolean} [disabled] - render this node's inputs read-only
 * @param {string[]} [hiddenInputKeys] - input keys to omit from this list
 *   because another control owns them (e.g. inputs driven by the job's
 *   `anotherDateSelection` mission picker).
 */
export function JobNodeInputs({
    jobName,
    moduleVersionInputs,
    disabled,
    hiddenInputKeys,
}) {
    const isBasicRole = useIsBasicRole();
    const [showAdditional, setShowAdditional] = useState(false);

    // Deep copy moduleVersionInputs to avoid modifying the input argument
    const inputs = structuredClone(moduleVersionInputs);

    // Add input keys on input objects for easier reference
    Object.entries(inputs).forEach(([key, input]) => {
        input.key = key;
    });

    const sortInputs = (arr) =>
        arr.sort((a, b) => {
            if (a.position != null && b.position != null) {
                return a.position - b.position;
            }
            if (a.position != null) return -1;
            if (b.position != null) return 1;
            return a.key.localeCompare(b.key);
        });

    const hiddenKeys = new Set(hiddenInputKeys);

    const alwaysInputs = [];
    const additionalInputs = [];
    Object.values(inputs).forEach((input) => {
        if (hiddenKeys.has(input.key)) return;
        const level = input.displayLevel || "collapseDisplayed";
        if (level === "neverDisplayed") return;
        if (level === "onlyAdmin" && isBasicRole) return;
        if (level === "alwaysDisplayed") alwaysInputs.push(input);
        else additionalInputs.push(input);
    });
    sortInputs(alwaysInputs);
    sortInputs(additionalInputs);

    return (
        <Stack className="nodrag nopan nowheel" sx={{ p: 1 }} spacing={1}>
            {alwaysInputs.map((input) => (
                <ModuleInput
                    key={input.key}
                    jobName={jobName}
                    input={input}
                    disabled={disabled}
                />
            ))}
            {additionalInputs.length > 0 && (
                <Button
                    size="small"
                    variant="text"
                    startIcon={showAdditional ? <ExpandLess /> : <ExpandMore />}
                    onClick={() => setShowAdditional((v) => !v)}
                >
                    {showAdditional
                        ? "Hide additional inputs"
                        : "Show additional inputs"}
                </Button>
            )}
            {showAdditional &&
                additionalInputs.map((input) => (
                    <ModuleInput
                        key={input.key}
                        jobName={jobName}
                        input={input}
                        disabled={disabled}
                    />
                ))}
        </Stack>
    );
}
